import type { ComponentProps } from 'react';

import { InventoryInstancePanel } from '@/app/(dashboard)/inventory/[[...ids]]/inventoryInstancePanel';
import { BackupJobsCard } from '@/app/(dashboard)/jobs/[tab]/backupJobsCard';
import { RestoreJobsCard } from '@/app/(dashboard)/jobs/[tab]/restoreJobsCard';
import { ScanJobsCard } from '@/app/(dashboard)/jobs/[tab]/scanJobsCard';
import BackupPolicyPanel from '@/app/(dashboard)/settings/backup-policies/BackupPolicySidePanel';
import BackupScopeResourcesWizard from '@/app/(dashboard)/settings/backup-policies/backup-scopes/[tab]/components/backupScopeResourcesWizard';
import ControlWizard from '@/app/(dashboard)/settings/controls/ControlWizard';
import ObjectStoreBackupConfigurationPanel from '@/app/(dashboard)/settings/object-store-backup-configuration/objectStoreBackupConfigurationPanel';
import RansomwareByConditionPanel from '@/app/(dashboard)/settings/ransomware-detection/ransomwareByConditionPanel';
import { CreateAccountPanel } from '@/components/accounts/createAccountPanel';
import { UpdateAccountAWSPanel } from '@/components/accounts/updateAccountAWSPanel';

import ConvertAllToEonSnapshotsPanel from './ConvertAllToEonSnapshotsPanel';
import ConvertToSingleEonSnapshotPanel from './ConvertToSingleEonSnapshotPanel';
import type { Panels } from './panels';

import { RestoreAccountConfigPanel } from '../accounts/restore/restoreAccountConfigPanel';
import { ApiCredentialsPanel } from '../apiCredentials/apiCredentialsPanel';
import { FiltersPanel } from '../filters/filtersPanel';
import { UpsertGroupMappingPanel } from '../groups/upsertGroupMappingPanel';
import { UpsertNotificationPolicyPanel } from '../notifications/upsertNotificationPolicyPanel';
import { CustomRolePanel } from '../permissions/customRolePanel';
import { ConvertToAMIWizard } from '../restore/convertToAMIWizard';
import { RestoreAzureDiskWizard } from '../restore/restoreAzureDiskWizard';
import { RestoreAzureSqlDBWizard } from '../restore/restoreAzureSqlDbWizard';
import { RestoreAzureVMWizard } from '../restore/restoreAzureVMWizard';
import { RestoreDynamoWizard } from '../restore/restoreDynamoWizard';
import { RestoreEc2InstanceWizard } from '../restore/restoreEc2InstanceWizard';
import { RestoreEksNamespaceWizard } from '../restore/restoreEksNamespaceWizard';
import { RestoreFileWizard } from '../restore/restoreFileWizard';
import { RestoreGcpCloudSqlWizard } from '../restore/restoreGcpCloudSqlWizard';
import { RestoreGcpDiskWizard } from '../restore/restoreGcpDiskWizard';
import { RestoreGcpVMWizard } from '../restore/restoreGcpVMWizard';
import { RestoreMongoAtlasWizard } from '../restore/restoreMongoAtlasWizard';
import { RestoreRDSWizard } from '../restore/restoreRDSWizard';
import { RestoreS3Wizard } from '../restore/restoreS3Wizard';
import { RestoreVolumeWizard } from '../restore/restoreVolumeWizard';
import { SamlPanel } from '../saml/samlPanel';
import { UpsertUserPanel } from '../users/upsertUserPanel';
import { EditBackupVaultPanel } from '../vaults/editBackupVaultPanel';

type PanelsMap<T extends PanelTypes> = {
  [k in T['panel']]: (
    props: Extract<PanelTypes, { panel: k }>['props']
  ) => React.ReactNode;
};

export const PanelsMap: PanelsMap<PanelTypes> = {
  InventoryInstance: InventoryInstancePanel,
  RestoreVolumeWizard: RestoreVolumeWizard,
  RestoreEc2InstanceWizard: RestoreEc2InstanceWizard,
  RestoreFileWizard: RestoreFileWizard,
  RestoreRDSWizard: RestoreRDSWizard,
  RestoreDynamoWizard: RestoreDynamoWizard,
  RestoreS3Wizard: RestoreS3Wizard,
  ConvertToAMIWizard: ConvertToAMIWizard,
  RestoreMongoAtlasWizard: RestoreMongoAtlasWizard,
  RestoreAzureVMWizard: RestoreAzureVMWizard,
  RestoreAzureDiskWizard: RestoreAzureDiskWizard,
  RestoreAzureSqlDBWizard: RestoreAzureSqlDBWizard,
  EditBackupVault: EditBackupVaultPanel,
  BackupPolicyConfig: BackupPolicyPanel,
  CustomRole: CustomRolePanel,
  ApiCredentials: ApiCredentialsPanel,
  Control: ControlWizard,
  NotificationPolicy: UpsertNotificationPolicyPanel,
  SAML: SamlPanel,
  Filters: FiltersPanel,
  CloudAccount: CreateAccountPanel,
  UpsertUser: UpsertUserPanel,
  UpsertGroupMapping: UpsertGroupMappingPanel,
  RestoreAccountConfig: RestoreAccountConfigPanel,
  ConvertToMultipleEonSnapshots: ConvertAllToEonSnapshotsPanel,
  ConvertToSingleEonSnapshot: ConvertToSingleEonSnapshotPanel,
  RestoreEksNamespaceWizard: RestoreEksNamespaceWizard,
  ObjectStoreBackupConfiguration: ObjectStoreBackupConfigurationPanel,
  RestoreGcpVMWizard: RestoreGcpVMWizard,
  RestoreGcpDiskWizard: RestoreGcpDiskWizard,
  RestoreGcpCloudSqlWizard: RestoreGcpCloudSqlWizard,
  UpdateAccountAWSPanel: UpdateAccountAWSPanel,
  RansomwareByConditionPanel: RansomwareByConditionPanel,
  BackupScopeResourcesWizard: BackupScopeResourcesWizard,
  BackupJobsCard: BackupJobsCard,
  ScanJobsCard: ScanJobsCard,
  RestoreJobsCard: RestoreJobsCard,
};

export type PanelTypes =
  | {
      panel: typeof Panels.InventoryInstance;
      props: ComponentProps<typeof InventoryInstancePanel>;
    }
  | {
      panel: typeof Panels.EditBackupVault;
      props: ComponentProps<typeof EditBackupVaultPanel>;
    }
  | {
      panel: typeof Panels.RestoreVolumeWizard;
      props: ComponentProps<typeof RestoreVolumeWizard>;
    }
  | {
      panel: typeof Panels.RestoreEc2InstanceWizard;
      props: ComponentProps<typeof RestoreEc2InstanceWizard>;
    }
  | {
      panel: typeof Panels.RestoreFileWizard;
      props: ComponentProps<typeof RestoreFileWizard>;
    }
  | {
      panel: typeof Panels.RestoreRDSWizard;
      props: ComponentProps<typeof RestoreRDSWizard>;
    }
  | {
      panel: typeof Panels.RestoreDynamoWizard;
      props: ComponentProps<typeof RestoreDynamoWizard>;
    }
  | {
      panel: typeof Panels.RestoreS3Wizard;
      props: ComponentProps<typeof RestoreS3Wizard>;
    }
  | {
      panel: typeof Panels.RestoreMongoAtlasWizard;
      props: ComponentProps<typeof RestoreMongoAtlasWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureVMWizard;
      props: ComponentProps<typeof RestoreAzureVMWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureDiskWizard;
      props: ComponentProps<typeof RestoreAzureDiskWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureSqlDBWizard;
      props: ComponentProps<typeof RestoreAzureSqlDBWizard>;
    }
  | {
      panel: typeof Panels.ConvertToAMIWizard;
      props: ComponentProps<typeof ConvertToAMIWizard>;
    }
  | {
      panel: typeof Panels.NotificationPolicy;
      props: ComponentProps<typeof UpsertNotificationPolicyPanel>;
    }
  | {
      panel: typeof Panels.SAML;
      props: ComponentProps<typeof SamlPanel>;
    }
  | {
      panel: typeof Panels.Filters;
      props: ComponentProps<typeof FiltersPanel>;
    }
  | {
      panel: typeof Panels.BackupPolicyConfig;
      props: ComponentProps<typeof BackupPolicyPanel>;
    }
  | {
      panel: typeof Panels.CustomRole;
      props: ComponentProps<typeof CustomRolePanel>;
    }
  | {
      panel: typeof Panels.ApiCredentials;
      props: ComponentProps<typeof ApiCredentialsPanel>;
    }
  | {
      panel: typeof Panels.Control;
      props: ComponentProps<typeof ControlWizard>;
    }
  | {
      panel: typeof Panels.CloudAccount;
      props: ComponentProps<typeof CreateAccountPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountAWSPanel;
      props: ComponentProps<typeof UpdateAccountAWSPanel>;
    }
  | {
      panel: typeof Panels.UpsertUser;
      props: ComponentProps<typeof UpsertUserPanel>;
    }
  | {
      panel: typeof Panels.UpsertGroupMapping;
      props: ComponentProps<typeof UpsertGroupMappingPanel>;
    }
  | {
      panel: typeof Panels.RestoreAccountConfig;
      props: ComponentProps<typeof RestoreAccountConfigPanel>;
    }
  | {
      panel: typeof Panels.ConvertToMultipleEonSnapshots;
      props: ComponentProps<typeof ConvertAllToEonSnapshotsPanel>;
    }
  | {
      panel: typeof Panels.ConvertToSingleEonSnapshot;
      props: ComponentProps<typeof ConvertToSingleEonSnapshotPanel>;
    }
  | {
      panel: typeof Panels.RestoreEksNamespaceWizard;
      props: ComponentProps<typeof RestoreEksNamespaceWizard>;
    }
  | {
      panel: typeof Panels.ObjectStoreBackupConfiguration;
      props: ComponentProps<typeof ObjectStoreBackupConfigurationPanel>;
    }
  | {
      panel: typeof Panels.RestoreGcpVMWizard;
      props: ComponentProps<typeof RestoreGcpVMWizard>;
    }
  | {
      panel: typeof Panels.RestoreGcpDiskWizard;
      props: ComponentProps<typeof RestoreGcpDiskWizard>;
    }
  | {
      panel: typeof Panels.RestoreGcpCloudSqlWizard;
      props: ComponentProps<typeof RestoreGcpCloudSqlWizard>;
    }
  | {
      panel: typeof Panels.RansomwareConditionPanel;
      props: ComponentProps<typeof RansomwareByConditionPanel>;
    }
  | {
      panel: typeof Panels.BackupScopeResourcesWizard;
      props: ComponentProps<typeof BackupScopeResourcesWizard>;
    }
  | {
      panel: typeof Panels.ScanJobsCard;
      props: ComponentProps<typeof ScanJobsCard>;
    }
  | {
      panel: typeof Panels.BackupJobsCard;
      props: ComponentProps<typeof BackupJobsCard>;
    }
  | {
      panel: typeof Panels.RestoreJobsCard;
      props: ComponentProps<typeof RestoreJobsCard>;
    };
